import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Stack, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import useCopy from '../../hooks/useCopy';
import initials from '../../utils/initials';
import TruncatedText from '../common/TruncatedText';
import { formatDate, fromNow } from '../../utils/dates';
import { connectionHealth } from './health';

export function accountTitle(connection) {
  return connection.name || connection.user_email || 'Unknown account';
}

// Avatar with the account's name and, when it has a name, its email below; width fixes it for a table column
export function AccountIdentity({ connection, color, width }) {
  const title = accountTitle(connection);
  const subtitle = connection.name ? connection.user_email : null;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center"
           sx={width ? { width, minWidth: width, maxWidth: width } : { minWidth: 0 }}>
      <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: alpha(color, 0.12), color }}>
        {initials(title)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <TruncatedText variant="body2" sx={{ fontWeight: 600 }}>{title}</TruncatedText>
        {subtitle && <TruncatedText variant="body2" color="text.secondary">{subtitle}</TruncatedText>}
      </Box>
    </Stack>
  );
}

export default function AccountDetailsDialog({ connection, meta, open, busy, disabled, onClose, onReconnect }) {
  if (!connection) return null;
  const health = connectionHealth(connection);
  const titleId = `account-${connection.id}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}>
      <DialogTitle id={titleId} component="div" sx={{ pr: 7 }}>
        <AccountIdentity connection={connection} color={meta.color} />
        <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', right: 12, top: 14 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {health.prompt && <Alert severity={health.level} sx={{ mb: 2 }}>{health.prompt}</Alert>}
        <AccountDetails connection={connection} meta={meta} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onReconnect} disabled={disabled}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}>
          Reconnect
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AccountDetails({ connection, meta }) {
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' },
      rowGap: { xs: 0.5, sm: 2 }, columnGap: 2, fontSize: 14,
      '& dt': { color: 'text.secondary', mt: { xs: 1.5, sm: 0 } }, '& dd': { m: 0, minWidth: 0 } }}>
      <dt>{meta.accountLabel ?? 'Account ID'}</dt>
      <dd><CopyValue value={connection.object_id} /></dd>
      <dt>Ad accounts</dt>
      <dd>{connection.ad_account_count ?? '—'}</dd>
      <dt>Permissions</dt>
      <dd>
        {connection.scopes.length ? (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {connection.scopes.map((scope) => (
              <Chip key={scope} label={scope} size="small" variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: 12 }} />
            ))}
          </Stack>
        ) : '—'}
      </dd>
      <dt>Connected</dt>
      <dd><When value={connection.sso_updated_on || connection.issued_at} /></dd>
      <dt>Last checked</dt>
      <dd><When value={connection.checked_at} /></dd>
      <dt>Token expires</dt>
      <dd>{connection.expires_at ? <When value={connection.expires_at} /> : 'Never'}</dd>
      <dt>Data access until</dt>
      <dd><When value={connection.data_access_expires_at} /></dd>
      <dt>Connected via</dt>
      <dd>{connection.is_manual ? 'Pasted access token' : `${meta.name} login`}</dd>
      <dt>App</dt>
      <dd>{connection.app_name || '—'}</dd>
    </Box>
  );
}

function When({ value }) {
  if (!value) return '—';
  return (
    <>
      {formatDate(value)}
      <Typography component="span" variant="body2" color="text.secondary"> · {fromNow(value)}</Typography>
    </>
  );
}

function CopyValue({ value }) {
  const [copyState, copy] = useCopy();
  if (!value) return '—';
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{value}</Typography>
      <Tooltip title={{ copied: 'Copied', failed: 'Could not copy' }[copyState] ?? 'Copy'}>
        <IconButton size="small" onClick={() => copy(value)} aria-label="Copy ID" sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
